import { Outlet, useLocation, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { useClubOnboarding } from "@/features/onboarding/hooks/useClubOnboarding";
import { SetupGuard } from "@/features/onboarding/pages/WelcomeShell";
import {
  UploadQueueProvider,
  useUploadQueueContext,
} from "@/features/coach/components/UploadQueueContext";
import { UploadQueuePanel } from "@/features/coach/components/UploadQueuePanel";
import { NotFoundPage } from "@/features/landing/pages/NotFoundPage";
import { AdminSidebar } from "./components/AdminSidebar";
import type { AdminSectionId } from "./nav";
import {
  ADMIN_SECTIONS,
  adminConsoleTitle,
  canSeeAdminSection,
  defaultAdminLanding,
  hasAdminAccess,
  LEGACY_SECTION_REDIRECTS,
} from "./nav";

/**
 * M8a F1 / F3 / F4 / F5 / F9 — the /admin parent shell.
 *
 * Lifecycle responsibilities:
 *   - Show inline-login if `/api/me` is 401 (M4 pattern; replaced
 *     by the real M9 login UI later).
 *   - Redirect away from /admin entirely if the user has no admin
 *     access (plain coach / viewer).
 *   - Redirect /admin (root) → the user's default landing (global
 *     admin → overview, team admin → people, uploader → matches).
 *   - Honour LEGACY_SECTION_REDIRECTS (/admin/streams → /admin/live,
 *     /admin/system → /admin/performance).
 *   - Render the always-visible header (title + status strip),
 *     sidebar, and sub-route outlet.
 *
 * The status strip is mounted HERE, not per sub-route, so navigation
 * between admin pages doesn't refetch the 10 s diagnostics + streams
 * loops (one cache key, shared across the shell).
 */
export function AdminShell() {
  const onboarding = useClubOnboarding();
  const me = onboarding.me;
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;
  const isAdminPath = pathname === "/admin" || pathname.startsWith("/admin/");
  // Multi-club: /admin is the per-club console, only meaningful on a club
  // host. On the apex / super-admin host `current_club` is null and the shell
  // 404s below; the admin-navigation effects (default landing, welcome,
  // per-section gate) must NOT fire there, or they'd redirect a super-admin
  // away from the 404 into a club surface that has no host club.
  const hasHostClub = !!me.data?.current_club;

  // M9a A8 — auth gate. Unauthenticated users redirect to
  // `/login?next=<current path>` so they land back on the admin
  // page they were trying to reach. Replaces the M4
  // <InlineLoginPanel> that previously rendered inline.
  //
  // Capture the ORIGINAL pathname at mount via a ref so a second
  // fire of this effect (after the navigate updates `pathname`)
  // doesn't overwrite `?next=` with `/login`. TanStack Router
  // keeps the parent route mounted briefly across navigation,
  // and `useLocation` updates before the unmount.
  const authGateMountPath = useRef(pathname);
  const authRedirectFired = useRef(false);
  useEffect(() => {
    if (me.isLoading || (me.isError && !onboarding.anonymous)) return;
    if (!isAdminPath) return;
    if (me.data?.user) return;
    if (authRedirectFired.current) return;
    authRedirectFired.current = true;
    void navigate({
      to: "/login",
      search: { next: authGateMountPath.current || "/admin" },
      replace: true,
    });
  }, [
    me.isLoading,
    me.isError,
    me.data,
    onboarding.anonymous,
    isAdminPath,
    navigate,
  ]);

  // F5 — legacy URL redirects.
  useEffect(() => {
    const redirect = LEGACY_SECTION_REDIRECTS[pathname];
    if (redirect) {
      void navigate({ to: redirect, replace: true });
    }
  }, [pathname, navigate]);

  const welcomePending = isAdminPath && onboarding.autoStart;
  useEffect(() => {
    if (welcomePending) void navigate({ to: "/welcome", replace: true });
  }, [welcomePending, navigate]);

  // F4 — default landing for bare /admin. Skipped while the welcome
  // redirect owns the next navigation (would otherwise overwrite
  // /welcome with /admin/overview in the same flush). Also skipped
  // for users without admin access — F3 below bounces them to /hub;
  // without this guard F4 races F3 and the URL momentarily flips to
  // /admin/matches before F3's `replace: true` overwrites it, which
  // is exactly the blank-content-area dead-end #209 reported (the
  // render guard at L230 below returns null for no-access users, but
  // the URL change still happens, so the user sees a blank admin
  // shell at /admin/matches for the duration of the second navigate).
  useEffect(() => {
    if (pathname !== "/admin") return;
    if (!me.data) return;
    if (!hasHostClub) return; // apex host → shell 404s; don't redirect.
    if (
      welcomePending ||
      (onboarding.allowed && (onboarding.loading || onboarding.error))
    )
      return;
    if (!hasAdminAccess(me.data)) return;
    const landing = defaultAdminLanding(me.data);
    const section = ADMIN_SECTIONS.find((s) => s.id === landing);
    if (section) {
      void navigate({ to: section.path, replace: true });
    }
  }, [
    pathname,
    me.data,
    hasHostClub,
    welcomePending,
    onboarding.allowed,
    onboarding.loading,
    onboarding.error,
    navigate,
  ]);

  // F3 — hard gate: a signed-in user with no admin access gets
  // bounced to `/hub`, the role-based redirect, so they land on
  // their OWN home surface (coach → /coach/review, player/guardian
  // → /feedback) instead of the public marketing landing at `/`.
  // `me.data` is truthy here (the auth gate above already handled
  // anonymous users), so `/hub` always resolves to a real surface.
  useEffect(() => {
    if (!isAdminPath) return;
    if (!me.data) return;
    if (!hasAdminAccess(me.data)) {
      void navigate({ to: "/hub", replace: true });
    }
  }, [isAdminPath, me.data, navigate]);

  // Re-review P2 — per-section gate. The sidebar hides links the
  // user can't see, but a direct URL nav (or a stale bookmark) to
  // `/admin/live` still mounts the route and starts its admin
  // queries. Redirect denied sections to the user's default landing
  // so manual URLs follow the same gates as the sidebar. Skip
  // legacy-redirect paths (the other effect rewrites them first),
  // the bare-/admin redirect (the F4 effect handles it), and
  // no-admin-access users (F3 above bounces them to /hub).
  useEffect(() => {
    if (!me.data) return;
    if (!isAdminPath) return;
    if (!hasHostClub) return; // apex host → shell 404s; don't redirect.
    if (pathname === "/admin" || pathname === "/admin/") return;
    if (LEGACY_SECTION_REDIRECTS[pathname]) return;
    if (!hasAdminAccess(me.data)) return;
    const section = activeSectionForPath(pathname);
    if (!canSeeAdminSection(section, me.data)) {
      const landing = defaultAdminLanding(me.data);
      const target = ADMIN_SECTIONS.find((s) => s.id === landing);
      if (target && target.path !== pathname) {
        void navigate({ to: target.path, replace: true });
      }
    }
  }, [pathname, isAdminPath, me.data, hasHostClub, navigate]);

  if (me.isLoading) {
    return (
      <div data-testid="admin-shell-loading" className="p-6 text-ink-muted">
        Loading…
      </div>
    );
  }

  if (onboarding.error && (!me.data?.user || !onboarding.teams.data)) {
    return (
      <SetupGuard
        error
        retry={() => {
          void me.refetch();
          if (onboarding.allowed) void onboarding.teams.refetch();
        }}
      />
    );
  }

  // 401 boundary — the M9a `/login?next=` redirect effect above
  // fires. Render nothing rather than flashing the shell to an
  // anonymous user.
  if (!me.data?.user) {
    return null;
  }

  if (!hasAdminAccess(me.data)) {
    // The redirect effect above is firing; render nothing rather
    // than flashing the shell to a user who can't see it.
    return null;
  }

  // Multi-club: /admin is the per-CLUB console. On the apex / super-admin
  // host there is NO host club (`current_club == null`), so the console has
  // nothing to scope to — rendering it shows ambiguous platform-default
  // branding ("which club am I editing?"). 404 instead, the mirror of how
  // /super-admin 404s on a club host. A super-admin manages clubs via
  // /super-admin and edits a specific club's settings on that club's own
  // subdomain (where `current_club` is set and the super-admin override
  // grants club_admin).
  if (!me.data.current_club) {
    return <NotFoundPage />;
  }

  if (
    onboarding.allowed &&
    (onboarding.loading || (onboarding.error && !onboarding.teams.data))
  ) {
    return (
      <SetupGuard
        error={onboarding.error}
        retry={() => {
          void onboarding.me.refetch();
          void onboarding.teams.refetch();
        }}
      />
    );
  }
  if (welcomePending) return <SetupGuard />;
  const active = activeSectionForPath(pathname);

  return (
    // UploadQueueProvider hoists the multi-upload queue above the
    // `<Outlet />` so it survives navigation between /admin sections —
    // the queue's in-memory File objects (and the active chunk loop)
    // would otherwise be dropped every time AdminMatchesPage unmounts.
    <UploadQueueProvider>
      <main data-testid="admin-shell" className="min-h-screen bg-canvas">
        {/* The redundant "ADMIN CONSOLE" title strip was removed once the
         * global `PublicAppHeader` mounted on every route — the active
         * Admin link in that header already conveys the operator context,
         * and stacking two header bars produced the double-chrome the PR
         * review flagged. Operator identity / multi-team note still lives
         * in the screen-reader fallback below for the title computation. */}
        <h1 className="sr-only">{adminConsoleTitle(me.data)}</h1>
        {/* Section navigation + content share one shell. The approved-v5
         * design renders AdminSidebar as a horizontal bar above the active
         * page; the component keeps its legacy name for import stability. */}
        <div className="flex min-h-screen flex-col items-stretch md:flex-row">
          <AdminSidebar active={active} />
          <section
            data-testid="admin-content"
            data-admin-section={active}
            className="min-w-0 flex-1 p-3 sm:p-4"
          >
            {/* The contextual help link lives in each page's AdminPageHeader
             * right column (not an absolute overlay here), so it flows with
             * the header and never overlaps page meta/actions. */}
            {onboarding.error && (
              <div
                role="alert"
                className="mb-4 rounded bg-surface p-4 text-sm text-ink"
              >
                We couldn’t refresh your club’s setup status.
                <button
                  type="button"
                  className="ml-3 text-accent"
                  onClick={() => {
                    void me.refetch();
                    void onboarding.teams.refetch();
                  }}
                >
                  Try again
                </button>
              </div>
            )}
            <Outlet />
          </section>
        </div>
        {/* Docked queue panel — rendered once at shell level so it
         * stays visible (and uploads keep running) across route
         * changes. Hidden by the panel itself when the queue is empty. */}
        <ShellUploadQueuePanel />
      </main>
    </UploadQueueProvider>
  );
}

/** Renders the docked upload-queue panel from the shell-level queue
 * context. Split out so it can call `useUploadQueueContext()` inside
 * the provider. */
function ShellUploadQueuePanel() {
  const queue = useUploadQueueContext();
  return <UploadQueuePanel queue={queue} />;
}

function activeSectionForPath(path: string): AdminSectionId {
  // `/admin/foo/bar` → `foo`. Default to overview so an unknown
  // sub-path doesn't crash the sidebar's active highlight.
  const after = path.replace(/^\/admin\/?/, "");
  const head = after.split("/")[0] as AdminSectionId | "";
  const known = ADMIN_SECTIONS.find((s) => s.id === head);
  return known ? known.id : "overview";
}
